/**
 * Payment & Retention Dashboard — portfolio milestone invoice vs COP + retention held/released + working-capital drag.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Search, DollarSign, Wallet, TrendingDown, Info } from 'lucide-react';
import api from '@/lib/api';

const fmtINR = (v) => v == null ? '—' : (Math.abs(v) >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr` : Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(2)} L` : `₹${Number(v).toLocaleString('en-IN')}`);

export default function ContractsPayments() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => { (async () => {
    try { const r = await api.get('/contracts/portfolio/payments'); setData(r.data); }
    catch { toast.error('Failed to load payments'); } finally { setLoading(false); }
  })(); }, []);

  const rows = data?.payments || [];
  const filtered = useMemo(() => rows.filter(r => !q || (r.title || '').toLowerCase().includes(q.toLowerCase()) || (r.project || '').toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const t = data?.totals || {};

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="contracts-payments">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')} data-testid="pay-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Payment & Retention Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Milestone invoice vs COP · retention ledger · working-capital drag</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="pay-kpis">
          {[
            { label: 'Portfolio Value', value: fmtINR(t.portfolio_value), color: '#3B82F6', icon: DollarSign },
            { label: 'Billed', value: fmtINR(t.billed), color: '#8B5CF6', icon: DollarSign },
            { label: 'Certified (COP)', value: fmtINR(t.certified), color: '#22C55E', icon: DollarSign, sub: `Var ${fmtINR(t.cop_variance)}` },
            { label: 'Paid', value: fmtINR(t.paid), color: '#10B981', icon: Wallet },
            { label: 'Retention Held', value: fmtINR(t.retention_held), color: '#F59E0B', icon: Wallet, sub: `Released ${fmtINR(t.retention_released)}` },
            { label: 'Working-Capital Drag', value: fmtINR(t.wcd_amount), color: '#EF4444', icon: TrendingDown, sub: `${t.wcd_avg_days || 0}d avg` },
          ].map(kpi => (
            <Card key={kpi.label} className="bg-card border-border"><CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><kpi.icon className="w-3.5 h-3.5" style={{ color: kpi.color }} /><p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p></div>
              <p className="text-lg font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
              {kpi.sub && <p className="text-[10px] text-muted-foreground">{kpi.sub}</p>}
            </CardContent></Card>
          ))}
        </div>

        {data?.note && <Card className="bg-[#F59E0B10] border-[#F59E0B44]"><CardContent className="p-3 flex gap-2 items-center text-xs text-amber-200"><Info className="w-4 h-4 text-amber-400" />{data.note}</CardContent></Card>}

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search contract / project..." className="pl-10 bg-muted border-border text-foreground" data-testid="pay-search" />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="pay-table">
                <thead className="text-muted-foreground uppercase text-[10px]">
                  <tr>{['Contract', 'Project', 'Value', 'Progress', 'Billed', 'Certified (COP)', 'COP Var', 'Paid', 'Retention (Held / Rel.)', 'WCD (₹ / days)'].map(h => <th key={h} className="text-left px-2 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} className="border-t border-border hover:bg-white/[0.02] cursor-pointer" onClick={() => navigate(`/contracts/${r.id}`)} data-testid={`pay-row-${r.id}`}>
                      <td className="px-2 py-2 text-foreground">{r.title}<div className="text-[10px] text-muted-foreground">{r.contract_number}</div></td>
                      <td className="px-2 py-2 text-muted-foreground">{r.project}</td>
                      <td className="px-2 py-2 text-foreground/80">{fmtINR(r.value)}</td>
                      <td className="px-2 py-2 min-w-[100px]"><div className="flex items-center gap-2"><Progress value={r.progress_pct} className="h-1.5 w-14" /><span className="text-[10px] text-foreground/80">{r.progress_pct}%</span></div></td>
                      <td className="px-2 py-2 text-purple-400">{fmtINR(r.billed)}</td>
                      <td className="px-2 py-2 text-emerald-400">{fmtINR(r.certified)}</td>
                      <td className="px-2 py-2 font-semibold" style={{ color: r.cop_variance > 0 ? '#EF4444' : '#22C55E' }}>{fmtINR(r.cop_variance)}</td>
                      <td className="px-2 py-2 text-blue-400">{fmtINR(r.paid)}</td>
                      <td className="px-2 py-2 text-amber-400">{fmtINR(r.retention_held)} / <span className="text-emerald-400">{fmtINR(r.retention_released)}</span></td>
                      <td className="px-2 py-2 text-red-400">{fmtINR(r.wcd_amount)} / <span className="text-muted-foreground">{r.wcd_days}d</span></td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-muted-foreground">No contracts match search</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
